import { useEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/utils/cn';

interface ClickPosition {
  x: number;
  y: number;
}

interface ContainerSize {
  width: number;
  height: number;
}

interface RenderElementOnClickProps {
  children: ReactNode;
  className?: string;
}

interface UseGetElementOnClickReturn {
  RenderElementOnClick: React.FC<RenderElementOnClickProps>;
  setElementPosition: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * Hook que permite renderizar un elemento en un portal basado en la posición del click
 * El elemento se posiciona relativo a donde se hizo click y se cierra al hacer click fuera o al hacer scroll
 */
export const useGetElementOnClick = (): UseGetElementOnClickReturn => {
  const [clickPosition, setClickPosition] = useState<ClickPosition>({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState<ContainerSize>({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const RenderElementOnClick: React.FC<RenderElementOnClickProps> = ({ children, className }) => {
    useEffect(() => {
      if (!isVisible) return;
      if (containerRef?.current) {
        if (
          containerRef.current.clientWidth !== containerSize.width ||
          containerRef.current.clientHeight !== containerSize.height
        ) {
          setContainerSize({
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
          });
        }
      }

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const clickedElementPosition = target?.getBoundingClientRect();
        const differenceInXAxis = clickedElementPosition?.x - clickPosition.x;
        const differenceInYAxis = clickedElementPosition?.y - clickPosition.y;

        if (
          differenceInXAxis > 20 ||
          differenceInXAxis < -20 ||
          differenceInYAxis > 20 ||
          differenceInYAxis < -20
        ) {
          setIsVisible(false);
          setClickPosition({ x: 0, y: 0 });
        }
      };

      const handleScroll = () => {
        setIsVisible(false);
        setClickPosition({ x: 0, y: 0 });
      };

      window.addEventListener('click', handleClickOutside);
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('click', handleClickOutside);
        window.removeEventListener('scroll', handleScroll);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- solo re-ejecutar cuando isVisible; refs/valores mutables no como deps
    }, [isVisible]);

    if (!isVisible || !clickPosition.y || !clickPosition.x) return null;

    return createPortal(
      <div
        ref={containerRef}
        className={cn(
          'ui-fixed ui-z-[1] ui-rounded-lg',
          'ui-shadow-[0_0_15px_rgba(0,0,0,0.125),inset_0_0_1px_rgba(0,0,0,0.125)]',
          className
        )}
        style={{
          top: clickPosition.y,
          left: `${clickPosition.x - containerSize.width}px`,
        }}
      >
        {children}
      </div>,
      document.body
    );
  };

  const setElementPosition = (event: React.MouseEvent<HTMLElement>) => {
    const elementClick = event.currentTarget?.getBoundingClientRect();

    if (clickPosition.x > 0 && clickPosition.y > 0) {
      setIsVisible(false);
      setClickPosition({ x: 0, y: 0 });
    } else {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a small delay before showing the portal
      timeoutRef.current = setTimeout(() => {
        if (elementClick) {
          setClickPosition({
            x: elementClick.x,
            y: elementClick.y,
          });
          setIsVisible(true);
        }
      }, 50);
    }
  };

  return { RenderElementOnClick, setElementPosition };
};
